import React, { useMemo } from 'react';
import {
    BarChart2,
    Users,
    Clock,
    CheckCircle2,
    TrendingUp,
    Star,
    ThumbsUp,
    ThumbsDown,
    MessageSquare,
    Smile,
    Meh,
    Frown
} from 'lucide-react';

// Simple Progress Bar Component
const ProgressBar = ({ value, max = 100, color = 'bg-blue-500', height = 'h-2' }) => {
    const percentage = max > 0 ? (value / max) * 100 : 0;
    return (
        <div className={`w-full bg-gray-200 rounded-full ${height} overflow-hidden`}>
            <div
                className={`${color} ${height} rounded-full transition-all duration-500`}
                style={{ width: `${Math.min(percentage, 100)}%` }}
            />
        </div>
    );
};

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
    <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-start justify-between">
            <div>
                <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
                <h3 className="text-2xl font-bold text-gray-800">{value}</h3>
                {subtitle && (
                    <p className={`text-xs mt-1 ${trend === 'positive' ? 'text-green-600' : trend === 'negative' ? 'text-red-500' : 'text-gray-400'}`}>
                        {subtitle}
                    </p>
                )}
            </div>
            <div className={`p-2.5 rounded-lg ${color}`}>
                <Icon size={20} className="text-white" />
            </div>
        </div>
    </div>
);

// Horizontal Bar Chart for Rating Distribution
const RatingDistribution = ({ distribution, total }) => {
    const ratings = [5, 4, 3, 2, 1];
    const colors = ['bg-green-500', 'bg-lime-500', 'bg-yellow-500', 'bg-orange-500', 'bg-red-500'];

    return (
        <div className="space-y-2">
            {ratings.map((rating, idx) => {
                const count = distribution[rating] || 0;
                const percentage = total > 0 ? ((count / total) * 100).toFixed(0) : 0;
                return (
                    <div key={rating} className="flex items-center gap-3">
                        <div className="flex items-center gap-1 w-12">
                            <Star size={14} className="text-yellow-400 fill-yellow-400" />
                            <span className="text-sm text-gray-600">{rating}</span>
                        </div>
                        <div className="flex-1">
                            <ProgressBar value={count} max={total} color={colors[idx]} height="h-4" />
                        </div>
                        <span className="text-sm text-gray-500 w-12 text-right">{percentage}%</span>
                    </div>
                );
            })}
        </div>
    );
};

// Pie Chart-like Visualization for Multiple Choice
const MultipleChoiceChart = ({ options, responses }) => {
    const colors = ['bg-blue-500', 'bg-blue-500', 'bg-cyan-500', 'bg-teal-500', 'bg-emerald-500', 'bg-lime-500', 'bg-yellow-500', 'bg-orange-500'];
    const total = responses.length;

    // Count responses per option
    const counts = {};
    options.forEach(opt => counts[opt] = 0);
    responses.forEach(r => {
        const answer = r.response_text;
        // Try to match by option text or by number (e.g., "1" -> first option)
        if (counts.hasOwnProperty(answer)) {
            counts[answer]++;
        } else if (!isNaN(answer)) {
            const idx = parseInt(answer) - 1;
            if (idx >= 0 && idx < options.length) {
                counts[options[idx]]++;
            }
        }
    });

    return (
        <div className="space-y-3">
            {options.map((option, idx) => {
                const count = counts[option] || 0;
                const percentage = total > 0 ? ((count / total) * 100).toFixed(0) : 0;
                return (
                    <div key={idx} className="space-y-1">
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-700 truncate max-w-[80%]">{option}</span>
                            <span className="text-gray-500">{count} ({percentage}%)</span>
                        </div>
                        <ProgressBar value={count} max={total} color={colors[idx % colors.length]} height="h-3" />
                    </div>
                );
            })}
        </div>
    );
};

// Boolean Question Visualization
const BooleanChart = ({ responses }) => {
    const total = responses.length;
    let yesCount = 0;
    let noCount = 0;

    responses.forEach(r => {
        const answer = (r.response_text || '').toLowerCase();
        if (answer === 'yes' || answer === 'true' || answer === '1') {
            yesCount++;
        } else if (answer === 'no' || answer === 'false' || answer === '0') {
            noCount++;
        }
    });

    const yesPercent = total > 0 ? ((yesCount / total) * 100).toFixed(0) : 0;
    const noPercent = total > 0 ? ((noCount / total) * 100).toFixed(0) : 0;

    return (
        <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="flex-1 w-full sm:w-auto">
                <div className="flex items-center justify-between mb-2">
                    <span className="flex items-center gap-2 text-sm font-medium text-green-600">
                        <ThumbsUp size={16} /> Yes
                    </span>
                    <span className="text-sm text-gray-600">{yesCount} ({yesPercent}%)</span>
                </div>
                <ProgressBar value={yesCount} max={total} color="bg-green-500" height="h-4" />
            </div>
            <div className="flex-1 w-full sm:w-auto">
                <div className="flex items-center justify-between mb-2">
                    <span className="flex items-center gap-2 text-sm font-medium text-red-500">
                        <ThumbsDown size={16} /> No
                    </span>
                    <span className="text-sm text-gray-600">{noCount} ({noPercent}%)</span>
                </div>
                <ProgressBar value={noCount} max={total} color="bg-red-500" height="h-4" />
            </div>
        </div>
    );
};

// Sentiment Summary for Text Responses
const SentimentSummary = ({ responses }) => {
    let positive = 0, neutral = 0, negative = 0;

    responses.forEach(r => {
        const score = r.sentiment_score;
        if (score !== null && score !== undefined) {
            if (score > 0.2) positive++;
            else if (score < -0.2) negative++;
            else neutral++;
        } else {
            neutral++; // Default to neutral if no sentiment
        }
    });

    const total = positive + neutral + negative;
    const posPercent = total > 0 ? ((positive / total) * 100).toFixed(0) : 0;
    const neuPercent = total > 0 ? ((neutral / total) * 100).toFixed(0) : 0;
    const negPercent = total > 0 ? ((negative / total) * 100).toFixed(0) : 0;

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-4 justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-1">
                        <Smile size={24} className="text-green-600" />
                    </div>
                    <p className="text-lg font-bold text-gray-800">{posPercent}%</p>
                    <p className="text-xs text-gray-500">Positive</p>
                </div>
                <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-1">
                        <Meh size={24} className="text-gray-500" />
                    </div>
                    <p className="text-lg font-bold text-gray-800">{neuPercent}%</p>
                    <p className="text-xs text-gray-500">Neutral</p>
                </div>
                <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-1">
                        <Frown size={24} className="text-red-500" />
                    </div>
                    <p className="text-lg font-bold text-gray-800">{negPercent}%</p>
                    <p className="text-xs text-gray-500">Negative</p>
                </div>
            </div>
            <p className="text-xs text-center text-gray-400">{responses.length} text responses analyzed</p>
        </div>
    );
};

// Question Analytics Card
const QuestionAnalytics = ({ question, responses }) => {
    const questionResponses = responses.filter(r => r.question_id === question.id);

    // Parse options for multiple choice
    let options = [];
    if (question.options) {
        if (Array.isArray(question.options)) {
            options = question.options;
        } else if (typeof question.options === 'string') {
            try {
                options = JSON.parse(question.options);
            } catch {
                options = question.options.split(',').map(o => o.trim());
            }
        }
    }

    // Calculate rating stats
    const getRatingStats = () => {
        const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        let sum = 0;
        let count = 0;

        questionResponses.forEach(r => {
            const val = r.response_value || parseInt(r.response_text);
            if (val >= 1 && val <= 5) {
                distribution[val]++;
                sum += val;
                count++;
            }
        });

        return {
            distribution,
            average: count > 0 ? (sum / count).toFixed(1) : 0,
            total: count
        };
    };

    return (
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 mb-2 capitalize">
                        {question.question_type}
                    </span>
                    <h4 className="font-medium text-gray-800">{question.question_text}</h4>
                </div>
                <span className="text-sm text-gray-500">{questionResponses.length} responses</span>
            </div>

            {question.question_type === 'rating' && (
                <div className="space-y-4">
                    {(() => {
                        const stats = getRatingStats();
                        return (
                            <>
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="flex items-center gap-1">
                                        {[1, 2, 3, 4, 5].map(i => (
                                            <Star
                                                key={i}
                                                size={20}
                                                className={i <= Math.round(stats.average) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
                                            />
                                        ))}
                                    </div>
                                    <span className="text-2xl font-bold text-gray-800">{stats.average}</span>
                                    <span className="text-sm text-gray-500">average</span>
                                </div>
                                <RatingDistribution distribution={stats.distribution} total={stats.total} />
                            </>
                        );
                    })()}
                </div>
            )}

            {question.question_type === 'multiple_choice' && options.length > 0 && (
                <MultipleChoiceChart options={options} responses={questionResponses} />
            )}

            {question.question_type === 'boolean' && (
                <BooleanChart responses={questionResponses} />
            )}

            {question.question_type === 'text' && (
                <SentimentSummary responses={questionResponses} />
            )}

            {questionResponses.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">No responses yet</p>
            )}
        </div>
    );
};

// Main Analytics Component
const SurveyAnalytics = ({ survey, instances }) => {
    // Aggregate all responses from all instances
    const allResponses = useMemo(() => {
        return instances.flatMap(inst => inst.responses || []);
    }, [instances]);

    // Calculate overview stats
    const stats = useMemo(() => {
        const total = instances.length;
        const completed = instances.filter(i => i.status === 'completed').length;
        const inProgress = instances.filter(i => i.status === 'in_progress').length;
        const pending = instances.filter(i => i.status === 'pending').length;

        // Calculate average completion time for completed instances
        let avgTimeMinutes = 0;
        const completedInstances = instances.filter(i => i.started_at && i.completed_at);
        if (completedInstances.length > 0) {
            const totalMinutes = completedInstances.reduce((sum, i) => {
                const start = new Date(i.started_at);
                const end = new Date(i.completed_at);
                return sum + ((end - start) / 60000);
            }, 0);
            avgTimeMinutes = Math.round(totalMinutes / completedInstances.length);
        }

        return {
            total,
            completed,
            inProgress,
            pending,
            completionRate: total > 0 ? ((completed / total) * 100).toFixed(0) : 0,
            avgTimeMinutes,
            totalResponses: allResponses.length
        };
    }, [instances, allResponses]);

    return (
        <div className="space-y-6">
            {/* Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    title="Total Assigned"
                    value={stats.total}
                    subtitle={`${stats.completed} completed`}
                    icon={Users}
                    color="bg-blue-500"
                />
                <StatCard
                    title="Completion Rate"
                    value={`${stats.completionRate}%`}
                    subtitle={stats.inProgress > 0 ? `${stats.inProgress} in progress` : null}
                    icon={CheckCircle2}
                    color="bg-green-500"
                    trend={stats.completionRate >= 70 ? 'positive' : stats.completionRate < 30 ? 'negative' : null}
                />
                <StatCard
                    title="Avg. Completion Time"
                    value={stats.avgTimeMinutes > 0 ? `${stats.avgTimeMinutes} min` : '-'}
                    subtitle={stats.avgTimeMinutes > 0 ? 'per survey' : 'No data yet'}
                    icon={Clock}
                    color="bg-blue-500"
                />
                <StatCard
                    title="Total Responses"
                    value={stats.totalResponses}
                    subtitle={`${survey.questions?.length || 0} questions`}
                    icon={MessageSquare}
                    color="bg-blue-500"
                />
            </div>

            {/* Response Progress Bar */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Response Status Distribution</h3>
                <div className="flex h-6 rounded-full overflow-hidden bg-gray-100">
                    {stats.completed > 0 && (
                        <div
                            className="bg-green-500 flex items-center justify-center text-white text-xs font-medium"
                            style={{ width: `${(stats.completed / stats.total) * 100}%` }}
                        >
                            {stats.completed > 2 && `${stats.completed}`}
                        </div>
                    )}
                    {stats.inProgress > 0 && (
                        <div
                            className="bg-blue-500 flex items-center justify-center text-white text-xs font-medium"
                            style={{ width: `${(stats.inProgress / stats.total) * 100}%` }}
                        >
                            {stats.inProgress > 2 && `${stats.inProgress}`}
                        </div>
                    )}
                    {stats.pending > 0 && (
                        <div
                            className="bg-gray-300 flex items-center justify-center text-gray-600 text-xs font-medium"
                            style={{ width: `${(stats.pending / stats.total) * 100}%` }}
                        >
                            {stats.pending > 2 && `${stats.pending}`}
                        </div>
                    )}
                </div>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-green-500"></span> Completed ({stats.completed})</span>
                    <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-blue-500"></span> In Progress ({stats.inProgress})</span>
                    <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-gray-300"></span> Pending ({stats.pending})</span>
                </div>
            </div>

            {/* Question-by-Question Breakdown */}
            <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-blue-500" />
                    Question-by-Question Analysis
                </h3>
                <div className="space-y-4">
                    {survey.questions?.map((question, idx) => (
                        <QuestionAnalytics
                            key={question.id || idx}
                            question={question}
                            responses={allResponses}
                        />
                    ))}
                    {(!survey.questions || survey.questions.length === 0) && (
                        <p className="text-center text-gray-500 py-8">No questions in this survey</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SurveyAnalytics;
